import React, { createContext, useContext, useRef, useState, useEffect } from 'react';

const AudioContext = createContext();

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [hasAttemptedAutoplay, setHasAttemptedAutoplay] = useState(false);
  const [showSoundPrompt, setShowSoundPrompt] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  
  // Check if user previously declined sound
  const userDeclinedSound = localStorage.getItem('artourius_sound_declined') === 'true';

  // Setup audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setCurrentTrackIndex((prev) => (prev + 1) % playlist.length);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Autoplay music when component mounts
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && !hasAttemptedAutoplay) {
      setHasAttemptedAutoplay(true);
      
      // Don't show prompt if user previously declined
      if (userDeclinedSound) {
        return;
      }
      
      // Delay autoplay slightly to ensure audio is loaded
      const timer = setTimeout(() => {
        audio.play()
          .then(() => {
            setIsPlaying(true);
            setShowSoundPrompt(false);
          })
          .catch((error) => {
            // Autoplay was prevented by browser - show prompt for user interaction
            console.log('Autoplay prevented, showing sound prompt:', error);
            setIsPlaying(false);
            setAutoplayBlocked(true);
            setShowSoundPrompt(true);
          });
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [hasAttemptedAutoplay, userDeclinedSound]);

  // Function to enable sound after user interaction
  const enableSound = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.play()
        .then(() => {
          setIsPlaying(true);
          setShowSoundPrompt(false);
          setAutoplayBlocked(false);
        })
        .catch((error) => {
          console.error('Still cannot play:', error);
        });
    }
  };

  // Dismiss prompt without playing (and remember choice)
  const dismissPrompt = () => {
    setShowSoundPrompt(false);
    localStorage.setItem('artourius_sound_declined', 'true');
  };

  // Load new track when index changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const wasPlaying = isPlaying;
      audio.src = playlist[currentTrackIndex].file;
      audio.volume = volume;
      if (wasPlaying) {
        audio.play().catch((error) => {
          console.error('Playback failed:', error);
          setIsPlaying(false);
        });
      }
    }
  }, [currentTrackIndex]);

  // Update volume
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
    }
  }, [volume]);

  // Complete playlist with all 9 songs and their dedicated cover images
  // Artourius Design Intro plays first on page load
  const playlist = [
    {
      name: 'Artourius Design Intro',
      file: '/artourius-design-intro.mp3',
      album: 'Artourius',
      artwork: '/artourius-design-intro-cover.jpg'
    },
    {
      name: 'Diving into Beats',
      file: '/diving-into-beats.mp3',
      album: 'Artourius',
      artwork: '/diving-into-beats-cover.jpg'
    },
    {
      name: 'Instrumental Eternal',
      file: '/instrumental-eternal.mp3',
      album: 'Artourius',
      artwork: '/instrumental-eternal-cover.jpg'
    },
    {
      name: 'Voices of Dreaming',
      file: '/voices-of-dreaming.mp3',
      album: 'Artourius',
      artwork: '/voices-of-dreaming-cover.jpg'
    },
    {
      name: 'Mind Travel',
      file: '/mind-travel.mp3',
      album: 'Artourius',
      artwork: '/mind-travel-cover.jpg'
    },
    {
      name: 'Relaxing in the Clouds',
      file: '/relaxing-in-the-clouds.mp3',
      album: 'Artourius',
      artwork: '/relaxing-in-the-clouds-cover.jpg'
    },
    {
      name: 'Finding Inner Peace',
      file: '/finding-inner-peace.mp3',
      album: 'Artourius',
      artwork: '/finding-inner-peace-cover.jpg'
    },
    {
      name: 'Living Now',
      file: '/living-now.mp3',
      album: 'Artourius',
      artwork: '/living-now-cover.jpg'
    },
    {
      name: 'Artourius Design Speech',
      file: '/artourius-design-speech.mp3',
      album: 'Artourius',
      artwork: '/artourius-design-speech-cover.jpg'
    }
  ];

  const currentTrack = playlist[currentTrackIndex];

  const value = {
    audioRef,
    isPlaying,
    setIsPlaying,
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    currentTrackIndex,
    setCurrentTrackIndex,
    volume,
    setVolume,
    playlist,
    currentTrack,
    showSoundPrompt,
    enableSound,
    dismissPrompt,
    autoplayBlocked
  };

  return (
    <AudioContext.Provider value={value}>
      {/* Global audio element - persists across all pages */}
      <audio 
        ref={audioRef} 
        src={currentTrack.file}
        style={{ display: 'none' }}
      />
      
      {/* Sound Enable Prompt - appears when autoplay is blocked */}
      {showSoundPrompt && (
        <div 
          className="fixed bottom-24 left-1/2 transform -translate-x-1/2 z-[9999] animate-fade-in-up"
          style={{
            animation: 'fadeInUp 0.5s ease-out'
          }}
        >
          <div className="bg-black/90 backdrop-blur-xl border border-[hsl(45,100%,70%)]/50 rounded-2xl px-6 py-4 shadow-2xl flex items-center gap-4">
            <div className="w-12 h-12 bg-[hsl(45,100%,70%)] rounded-full flex items-center justify-center flex-shrink-0">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="text-black"
              >
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-white font-semibold text-sm">Enable Background Music?</span>
              <span className="text-gray-400 text-xs">Experience Artourius with ambient sounds</span>
            </div>
            <div className="flex gap-2 ml-2">
              <button
                onClick={enableSound}
                className="px-4 py-2 bg-[hsl(45,100%,70%)] text-black font-bold text-sm rounded-full hover:bg-[hsl(45,100%,80%)] transition-all"
              >
                Enable
              </button>
              <button
                onClick={dismissPrompt}
                className="px-4 py-2 bg-gray-800 text-white font-medium text-sm rounded-full hover:bg-gray-700 transition-all"
              >
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `}</style>
      
      {children}
    </AudioContext.Provider>
  );
};
